import { Add as AddIcon, Remove as RemoveIcon } from '@mui/icons-material';
import { Box, Button, IconButton, type SxProps, TextField, Typography } from '@mui/material';
import type { ReactElement } from 'react';
import { styles } from './styles';
import type { TextAreaSection, TextAreaViewProps } from './types';

export const TextAreaView = ({
  data,
  sx,
  onAddSection,
  onRemoveSection,
  onUpdateSection,
  isEdit = false,
}: TextAreaViewProps) => {
  const sections = data?.sections || [{ title: '', content: '' }];

  // Dynamic font size based on the number of sections and content length
  const getContentFontSize = (): string => {
    // If only one section, use larger font size
    if (sections.length <= 2) {
      return '0.75rem';
    }

    // For multiple sections, adjust based on content length
    const maxLength = Math.max(...sections.map(section => section.content?.length || 0));
    return maxLength > styles.LAYOUT_CONSTRAINTS.FONT_SIZE_THRESHOLD ? '0.5rem' : '0.8rem';
  };

  // Render a single section
  const renderSection = (
    section: TextAreaSection,
    index: number,
    isEditMode: boolean = false,
  ): ReactElement => {
    const sx = isEditMode ? styles.editSection : styles.section;
    return (
      <Box key={index} sx={sx} width='100%'>
        {isEditMode && sections.length > styles.LAYOUT_CONSTRAINTS.MIN_SECTIONS && (
          <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}>
            <IconButton
              size='small'
              onClick={() => onRemoveSection?.(index)}
              sx={styles.removeButton}
            >
              <RemoveIcon fontSize='small' />
            </IconButton>
          </Box>
        )}

        {isEditMode ? (
          <>
            <TextField
              label='Section Title (Optional)'
              variant='outlined'
              size='small'
              value={section.title}
              onChange={e => onUpdateSection?.(index, 'title', e.target.value)}
              placeholder='Enter section title...'
              slotProps={{ htmlInput: { maxLength: styles.CHARACTER_LIMITS.TITLE } }}
              helperText={`${section.title.length}/${styles.CHARACTER_LIMITS.TITLE} characters`}
              sx={{ ...styles.textField, ...styles.titleField }}
            />
            <TextField
              label='Section Content'
              variant='outlined'
              multiline
              minRows={4}
              value={section.content}
              onChange={e => onUpdateSection?.(index, 'content', e.target.value)}
              placeholder='Enter section content...'
              slotProps={{ htmlInput: { maxLength: styles.CHARACTER_LIMITS.CONTENT } }}
              helperText={`${section.content.length}/${styles.CHARACTER_LIMITS.CONTENT} characters`}
              sx={{ ...styles.textField, ...styles.contentField }}
            />
          </>
        ) : (
          <>
            {section.title && (
              <Typography variant='h6' component='div' sx={styles.title}>
                {section.title}
              </Typography>
            )}
            {section.content && (
              <Typography
                variant='body1'
                component='div'
                sx={{ ...styles.content, fontSize: getContentFontSize() }}
              >
                {section.content}
              </Typography>
            )}
            {!section.title && !section.content && (
              <Typography variant='body2' component='div' sx={styles.placeholder}>
                Text Area Component
              </Typography>
            )}
          </>
        )}
      </Box>
    );
  };

  // Render sections grid
  const renderSectionsGrid = (isEditMode: boolean = false): ReactElement => (
    <Box sx={{ ...styles.sections, gap: isEditMode ? 0.5 : 1.5 }}>
      {sections.map((section, index) => renderSection(section, index, isEditMode))}
    </Box>
  );

  // Edit mode
  if (isEdit) {
    return (
      <>
        <Box sx={{ ...styles.container, ...styles.editContainer, ...(sx || {}) } as SxProps}>
          {renderSectionsGrid(true)}
        </Box>

        {sections.length < styles.LAYOUT_CONSTRAINTS.MAX_SECTIONS && (
          <Box sx={styles.addButtonContainer}>
            <Button
              variant='contained'
              startIcon={<AddIcon />}
              onClick={onAddSection || undefined}
              size='small'
              sx={styles.addButton}
            >
              Add Section
            </Button>
          </Box>
        )}
      </>
    );
  }

  // View mode - show placeholder when empty
  const hasContent = sections.some(section => section.title || section.content);

  return (
    <Box sx={{ ...styles.container, ...sx }}>
      {!hasContent ? (
        <Typography variant='body2' component='div' sx={styles.placeholder}>
          Text Area Component
        </Typography>
      ) : sections.length === 1 ? (
        <Box sx={styles.section}>
          {sections[0]?.title && (
            <Typography variant='h6' component='div' sx={styles.title}>
              {sections[0].title}
            </Typography>
          )}
          {sections[0]?.content && (
            <Typography
              variant='body1'
              component='div'
              sx={{ ...styles.content, fontSize: getContentFontSize() }}
            >
              {sections[0].content}
            </Typography>
          )}
        </Box>
      ) : (
        renderSectionsGrid()
      )}
    </Box>
  );
};
